"use client";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ChevronIcon, ExternalIcon } from "./icons";
import { cn } from "./utils";

export interface MenuItem {
  label: string;
  description: string;
  href: string;
  external?: boolean;
  badge?: string;
  icon?: ReactNode;
}

/**
 * A header menu that opens on click, not on hover: a hover menu between the pointer and
 * what someone is reaching for is the thing people complain about, and it is unusable on
 * a touch screen.
 *
 * Closes on Escape, on a click outside, and on picking an item, and hands focus back to
 * the trigger so the keyboard does not get stranded in a closed menu.
 */
export function NavMenu({ label, items }: { label: string; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      trigger.current?.focus();
    };
    const onPointer = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  return (
    <div ref={wrap} className="relative">
      <button
        ref={trigger}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex cursor-pointer items-center gap-1 text-[13.5px] transition-colors",
          open ? "text-fg" : "text-fg-muted hover:text-fg",
        )}
      >
        {label}
        <ChevronIcon className={cn("size-4 transition-transform duration-200", open && "rotate-180")} />
      </button>

      {open && (
        <div
          id={panelId}
          className="absolute top-full left-1/2 z-50 w-[19rem] -translate-x-1/2 pt-3"
        >
          <div className="grid gap-0.5 rounded-xl border border-line bg-bg p-1.5 shadow-[0_16px_40px_-12px_rgb(0_0_0/0.18)]">
            {items.map((item) => (
              <a
                key={item.href}
                href={item.href}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noreferrer" : undefined}
                onClick={() => setOpen(false)}
                className="group/item grid gap-1 rounded-lg px-3 py-2.5 transition-colors hover:bg-bg-soft"
              >
                <span className="flex items-center gap-2 text-[13.5px] font-medium text-fg">
                  {item.label}
                  {item.badge && (
                    <span className="rounded-full bg-accent-soft px-1.5 py-px font-mono text-[10px] text-accent">
                      {item.badge}
                    </span>
                  )}
                  {item.external && <ExternalIcon className="size-3.5 text-fg-faint" />}
                </span>
                <span className="text-[12.5px] leading-snug text-fg-muted">{item.description}</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
